import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { 
  Package, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  Boxes,
  TrendingDown,
  TrendingUp
} from "lucide-react";

interface Order {
  id: number;
  orderNumber: string;
  product: string;
  quantity: number;
  unit: string;
  status: string;
  clientId?: number;
  supplierId?: number;
}

interface StockPile {
  id: number;
  name: string;
  areaId: number;
  materialType: string;
  currentQuantity: string;
  reservedQuantity?: string;
  unit: string;
  qualityStatus: string;
  status: string;
  areaName?: string;
}

interface StockAllocation {
  id: number;
  orderId: number;
  pileId: number;
  allocatedQuantity: string;
  deliveredQuantity: string;
  remainingQuantity: string;
  unit: string;
  status: string;
  scheduledDeliveryDate?: string;
  allocatedBy: string;
  allocatedAt: string;
  notes?: string;
  pile?: StockPile;
}

interface StockAllocationProps {
  order: Order;
  onClose?: () => void;
}

// Form schema for creating stock allocations
const allocationSchema = z.object({
  pileId: z.number().min(1, "Please select a stock pile"),
  allocatedQuantity: z.string().min(1, "Quantity is required"),
  scheduledDeliveryDate: z.string().optional(),
  priority: z.number().default(1),
  notes: z.string().optional()
});

type AllocationFormData = z.infer<typeof allocationSchema>;

export default function StockAllocation({ order, onClose }: StockAllocationProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState<StockAllocation | null>(null);

  const { toast } = useToast();

  // Form for creating/editing allocations
  const allocationForm = useForm<AllocationFormData>({
    resolver: zodResolver(allocationSchema),
    defaultValues: {
      pileId: 0,
      allocatedQuantity: "",
      scheduledDeliveryDate: "",
      priority: 1,
      notes: ""
    }
  });

  // Fetch stock allocations for this order
  const { data: allocations = [], isLoading: allocationsLoading, error: allocationsError } = useQuery<StockAllocation[]>({
    queryKey: ['/api/stock/allocations', { orderId: order.id }],
  });

  // Fetch available stock piles that match the order's material
  const { data: availablePiles = [], isLoading: pilesLoading } = useQuery<StockPile[]>({
    queryKey: ['/api/stock/piles/available', { materialType: order.product, unit: order.unit }],
  });

  // Create stock allocation mutation
  const createAllocationMutation = useMutation({
    mutationFn: (allocationData: AllocationFormData) => {
      const payload = {
        ...allocationData,
        orderId: order.id,
        allocatedBy: "current-user", // This should come from auth context
        remainingQuantity: allocationData.allocatedQuantity,
        unit: order.unit
      };
      return apiRequest('/api/stock/allocations', 'POST', payload);
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Stock allocation created successfully" });
      setShowCreateDialog(false);
      allocationForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/stock/allocations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/piles'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create stock allocation",
        variant: "destructive"
      });
    }
  });

  // Update allocation mutation
  const updateAllocationMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<AllocationFormData> }) =>
      apiRequest(`/api/stock/allocations/${id}`, 'PUT', data),
    onSuccess: () => {
      toast({ title: "Success", description: "Stock allocation updated successfully" });
      setShowEditDialog(false);
      setEditingAllocation(null);
      allocationForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/stock/allocations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/piles'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update stock allocation",
        variant: "destructive"
      });
    }
  });

  // Delete allocation mutation  
  const deleteAllocationMutation = useMutation({
    mutationFn: (id: number) => apiRequest(`/api/stock/allocations/${id}`, 'DELETE'),
    onSuccess: () => {
      toast({ title: "Success", description: "Stock allocation deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/allocations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/piles'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete stock allocation",
        variant: "destructive"
      });
    }
  });

  // Complete delivery mutation
  const completeDeliveryMutation = useMutation({
    mutationFn: ({ id, deliveredQuantity }: { id: number; deliveredQuantity: string }) =>
      apiRequest(`/api/stock/allocations/${id}/deliver`, 'POST', { deliveredQuantity }),
    onSuccess: () => {
      toast({ title: "Success", description: "Delivery completed successfully" });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/allocations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/piles'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stock/transactions'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error", 
        description: error.message || "Failed to complete delivery",
        variant: "destructive"
      });
    }
  });

  const handleEditAllocation = (allocation: StockAllocation) => {
    setEditingAllocation(allocation);
    allocationForm.reset({
      pileId: allocation.pileId,
      allocatedQuantity: allocation.allocatedQuantity,
      scheduledDeliveryDate: allocation.scheduledDeliveryDate || "",
      priority: 1,
      notes: allocation.notes || ""
    });
    setShowEditDialog(true);
  };

  const onCreateAllocation = (data: AllocationFormData) => {
    createAllocationMutation.mutate(data);
  };

  const onEditAllocation = (data: AllocationFormData) => {
    if (editingAllocation) {
      updateAllocationMutation.mutate({ id: editingAllocation.id, data });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'allocated': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'partial_delivery': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatQuantity = (quantity: string | number, unit: string) => {
    if (!quantity) return '0';
    const num = typeof quantity === 'string' ? parseFloat(quantity) : quantity;
    return `${num.toLocaleString()} ${unit}`;
  };

  const getTotalAllocated = () => {
    return allocations.reduce((sum, allocation) => 
      sum + parseFloat(allocation.allocatedQuantity || '0'), 0
    );
  };

  const getTotalDelivered = () => {
    return allocations.reduce((sum, allocation) => 
      sum + parseFloat(allocation.deliveredQuantity || '0'), 0
    );
  };

  const getRemainingToAllocate = () => {
    return Math.max(0, order.quantity - getTotalAllocated());
  };

  if (allocationsLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center py-8">
          <div className="text-muted-foreground">Loading stock allocations...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Order Summary */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Order #{order.orderNumber} - Stock Allocations
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Product: {order.product} | Required: {formatQuantity(order.quantity, order.unit)}
              </p>
            </div>
            <div className="flex gap-2">
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="button-create-allocation">
                    <Plus className="h-4 w-4 mr-2" />
                    Allocate Stock
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Stock Allocation</DialogTitle>
                  </DialogHeader>
                  <Form {...allocationForm}>
                    <form onSubmit={allocationForm.handleSubmit(onCreateAllocation)} className="space-y-4">
                      <FormField
                        control={allocationForm.control}
                        name="pileId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Stock Pile</FormLabel>
                            <Select onValueChange={(value) => field.onChange(parseInt(value))}>
                              <FormControl>
                                <SelectTrigger data-testid="select-pile">
                                  <SelectValue placeholder="Select stock pile" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {availablePiles.map((pile) => (
                                  <SelectItem key={pile.id} value={pile.id.toString()}>
                                    {pile.name} - {pile.areaName} ({formatQuantity(pile.currentQuantity, pile.unit)} available)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={allocationForm.control}
                        name="allocatedQuantity"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Quantity to Allocate ({order.unit})</FormLabel>
                            <FormControl>
                              <Input 
                                type="number" 
                                {...field} 
                                placeholder="Enter quantity"
                                max={getRemainingToAllocate()}
                                data-testid="input-allocation-quantity"
                              />
                            </FormControl>
                            <FormMessage />
                            <div className="text-sm text-muted-foreground">
                              Remaining to allocate: {formatQuantity(getRemainingToAllocate(), order.unit)}
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={allocationForm.control}
                        name="scheduledDeliveryDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Scheduled Delivery Date</FormLabel>
                            <FormControl>
                              <Input 
                                type="datetime-local" 
                                {...field} 
                                data-testid="input-scheduled-date"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={allocationForm.control}
                        name="notes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Notes</FormLabel>
                            <FormControl>
                              <Textarea 
                                {...field} 
                                placeholder="Optional notes about this allocation"
                                data-testid="input-allocation-notes"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                          Cancel
                        </Button>
                        <Button 
                          type="submit" 
                          disabled={createAllocationMutation.isPending}
                          data-testid="button-save-allocation"
                        >
                          {createAllocationMutation.isPending ? 'Creating...' : 'Create Allocation'}
                        </Button>
                      </div>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>
              {onClose && (
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Allocation Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600" data-testid="text-total-allocated">
                {formatQuantity(getTotalAllocated(), order.unit)}
              </div>
              <div className="text-sm text-muted-foreground">Total Allocated</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600" data-testid="text-total-delivered">
                {formatQuantity(getTotalDelivered(), order.unit)}
              </div>
              <div className="text-sm text-muted-foreground">Total Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600" data-testid="text-remaining-allocate">
                {formatQuantity(getRemainingToAllocate(), order.unit)}
              </div>
              <div className="text-sm text-muted-foreground">Remaining to Allocate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600" data-testid="text-completion-rate">
                {order.quantity > 0 ? Math.round((getTotalDelivered() / order.quantity) * 100) : 0}%
              </div>
              <div className="text-sm text-muted-foreground">Completion Rate</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Allocations Table */}
      <Card>
        <CardHeader>
          <CardTitle>Stock Allocations ({allocations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {allocationsError ? (
            <div className="text-center py-8 text-destructive">
              Error loading allocations: {allocationsError.message}
            </div>
          ) : allocations.length === 0 ? (
            <div className="text-center py-8">
              <Boxes className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Stock Allocations</h3>
              <p className="text-muted-foreground mb-4">
                Start by allocating stock from available piles to fulfill this order.
              </p>
              <Button onClick={() => setShowCreateDialog(true)} data-testid="button-create-first-allocation">
                <Plus className="h-4 w-4 mr-2" />
                Create First Allocation
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stock Pile</TableHead>
                  <TableHead>Allocated</TableHead>
                  <TableHead>Delivered</TableHead>
                  <TableHead>Remaining</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Scheduled</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allocations.map((allocation) => (
                  <TableRow key={allocation.id}>
                    <TableCell data-testid={`allocation-pile-${allocation.id}`}>
                      <div>
                        <div className="font-medium">{allocation.pile?.name || 'Unknown Pile'}</div>
                        <div className="text-sm text-muted-foreground">
                          {allocation.pile?.areaName || 'Unknown Area'} • {allocation.pile?.materialType}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell data-testid={`allocation-allocated-${allocation.id}`}>
                      <div className="flex items-center gap-2">
                        <TrendingDown className="h-4 w-4 text-red-500" />
                        {formatQuantity(allocation.allocatedQuantity, allocation.unit)}
                      </div>
                    </TableCell>
                    <TableCell data-testid={`allocation-delivered-${allocation.id}`}>
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-green-500" />
                        {formatQuantity(allocation.deliveredQuantity, allocation.unit)}
                      </div>
                    </TableCell>
                    <TableCell data-testid={`allocation-remaining-${allocation.id}`}>
                      {formatQuantity(allocation.remainingQuantity, allocation.unit)}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={getStatusColor(allocation.status)}
                        data-testid={`allocation-status-${allocation.id}`}
                      >
                        {allocation.status}
                      </Badge>
                    </TableCell>
                    <TableCell data-testid={`allocation-scheduled-${allocation.id}`}>
                      {allocation.scheduledDeliveryDate ? 
                        new Date(allocation.scheduledDeliveryDate).toLocaleDateString() : 
                        '-'
                      }
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditAllocation(allocation)}
                          data-testid={`button-edit-allocation-${allocation.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {allocation.status === 'allocated' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => completeDeliveryMutation.mutate({
                              id: allocation.id,
                              deliveredQuantity: allocation.remainingQuantity
                            })}
                            data-testid={`button-complete-delivery-${allocation.id}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              data-testid={`button-delete-allocation-${allocation.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Stock Allocation</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this allocation? This will free up the allocated stock.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteAllocationMutation.mutate(allocation.id)}
                                className="bg-destructive hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Allocation Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Stock Allocation</DialogTitle>
          </DialogHeader>
          <Form {...allocationForm}>
            <form onSubmit={allocationForm.handleSubmit(onEditAllocation)} className="space-y-4">
              <FormField
                control={allocationForm.control}
                name="allocatedQuantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Allocated Quantity ({order.unit})</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        {...field} 
                        data-testid="input-edit-allocation-quantity"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={allocationForm.control}
                name="scheduledDeliveryDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Scheduled Delivery Date</FormLabel>
                    <FormControl>
                      <Input 
                        type="datetime-local" 
                        {...field} 
                        data-testid="input-edit-scheduled-date"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={allocationForm.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea 
                        {...field} 
                        data-testid="input-edit-allocation-notes"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={updateAllocationMutation.isPending}
                  data-testid="button-update-allocation"
                >
                  {updateAllocationMutation.isPending ? 'Updating...' : 'Update Allocation'}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}